import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="dialog"
//
// Default open/close/escape/backdrop wiring for Common::Dialog::Component —
// a stackable dialog for a step on top of a modal or slideover (crop,
// preview). Reuses modal.scss's backdrop/animation classes so it reads as
// the same chrome as Common::Modal.
//
// A caller with cleanup to run before dismissing (destroying a cropper,
// revoking an object URL) passes the component's own `controller:` param as
// its own Stimulus identifier instead of the default "dialog" — the four
// crop dialogs (logo/avatar upload) do this, so this controller never
// connects there. It implements `cancel` and `backdropClose` with the same
// signatures below so the shared markup keeps working either way.
export default class extends Controller {
  static targets = [ "dialog", "backdrop" ]

  open(event) {
    if (event) event.preventDefault()
    this.dialogElement.showModal()
    document.body.style.overflow = "hidden"
    this.dispatch("opened")
  }

  close(event) {
    if (event) event.preventDefault()
    this.dialogElement.close()
    document.body.style.overflow = ""
    this.dispatch("closed")
  }

  // The native "cancel" event (Escape key) — same dismissal as the close button.
  cancel(event) {
    event.preventDefault()
    this.close()
  }

  // Only dismiss when the click landed on the backdrop itself, not the content.
  backdropClose(event) {
    if (event.target === this.backdropTarget) {
      this.close(event)
    }
  }

  // Common::Dialog::Component always puts `data-dialog-target="dialog"` on
  // the <dialog> itself, AND (for the default "dialog" controller) also
  // self-declares `data-controller="dialog"` on it — so a Stimulus outlet
  // (qr_scanner_controller.js's scan dialog) can reach it directly. That
  // self-declaration starts a NEW "dialog" scope right at the <dialog>
  // element, so when a caller wraps an external trigger button together
  // with the dialog in an ancestor that ALSO declares
  // `data-controller="dialog"` (the developer gallery's "Open Dialog"
  // button), Stimulus's own scoping (`Scope#containsElement`, which walks
  // up with `closest()`) resolves the target to the INNER self-scope, not
  // the outer one — the outer instance's `this.dialogTarget` throws
  // "Missing target element" even though the element is right there in the
  // DOM. A plain `querySelector` isn't scope-exclusive like that, so it
  // finds the <dialog> either way: when THIS controller instance's own
  // element already IS the <dialog> (the outlet / no-external-trigger
  // case) it's used directly; otherwise its subtree is searched for the
  // same target attribute Stimulus itself failed to resolve.
  get dialogElement() {
    if (this.element.matches("dialog")) return this.element
    return this.element.querySelector('[data-dialog-target~="dialog"]') || this.dialogTarget
  }
}
